import { ref, onUnmounted, watch, computed, nextTick } from 'vue'
import modalTrapFocus from './modalTrapFocus'
import Loader from '../Loader/Loader.vue'

export default {
    name: 'Modal',
    components: {
        Loader
    },
    props: {
        open: {
            type: Boolean
        },
        title: {
            type: String,
            default: ''
        },
        subtitle: {
            type: String,
            default: ''
        },
        size: {
            type: String,
            default: 'medium',
            validator: (value) => ['small', 'medium', 'large', 'small-large'].includes(value)
        },
        theme: {
            type: String,
            default: 'white',
            validator: (value) => ['white', 'default'].includes(value)
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        closeAriaLabel: {
            type: String,
            default: 'Luk Modal'
        }
    },
    emits: ['close', 'open'],
    setup(props, { emit }) {
        const modalElementRef = ref(null)
        let keydownHandler = null
        let closeHandler = null
        let clickHandler = null

        const setupEventListeners = (modalElement) => {
            keydownHandler = (e) => modalTrapFocus(e, modalElement)
            document.addEventListener('keydown', keydownHandler)

            closeHandler = (event) => {
                if (event.target.returnValue === '') {
                    closeModal()
                }
            }
            modalElement.addEventListener('close', closeHandler)

            clickHandler = (event) => {
                if (event.target === modalElement) {
                    modalElement.close()
                }
            }
            modalElement.addEventListener('click', clickHandler)
        }

        const cleanupEventListeners = () => {
            if (keydownHandler) {
                document.removeEventListener('keydown', keydownHandler)
                keydownHandler = null
            }
            if (closeHandler && modalElementRef.value) {
                modalElementRef.value.removeEventListener('close', closeHandler)
                closeHandler = null
            }
            if (clickHandler && modalElementRef.value) {
                modalElementRef.value.removeEventListener('click', clickHandler)
                clickHandler = null
            }
        }

        // Watch for changes in the modal open state
        watch(() => props.open, async (value) => {
            if (value) {
                // Wait for DOM to render
                await nextTick()
                if (modalElementRef.value) {
                    const scrollPos = window.scrollY
                    modalElementRef.value.showModal()
                    setupEventListeners(modalElementRef.value)

                    // Restore the scroll position
                    setTimeout(() => {
                        window.scrollTo(0, scrollPos)
                    }, 50)
                    document.querySelector('body').classList.add('no-scroll')
                    emit('open')
                }
            } else {
                cleanupEventListeners()
                document.querySelector('body').classList.remove('no-scroll')
            }
        })

        const modalStyles = computed(() => {
            let styles = ''

            if (props.size !== 'medium') {
                styles += `nyk-modal--${props.size} `
            }

            if (props.theme === 'white') {
                styles += 'nyk-modal--white '
            }

            if (props.subtitle) {
                styles += 'nyk-modal--subtitle '
            }

            return styles.trim()
        })

        const closeModal = () => {
            emit('close')
        }

        onUnmounted(() => {
            cleanupEventListeners()
        })

        return {
            modalElementRef,
            modalStyles,
            closeModal
        }
    },
}
